import styled from '@emotion/styled'
import { Theme as MuiTheme } from '@mui/material/styles'

import { colors, getColor, isNightMode, theme } from '../../../Theme'
import muiColors from '../../../theme/MuiColors'

// ---------------------------------------------------------------------------
// Local palette & layout constants
// ---------------------------------------------------------------------------
// Exact hex/rgba values preserved from the original inline styles, grouped here so they read as
// intentional rather than magic and have a single place to change. Candidates to migrate into the
// shared theme once equivalent tokens exist.
export const TEXT_PRIMARY = '#333'
export const TITLE_DIMMED = '#9d9d9d'
const DESC_DIMMED = '#7f7f7f'
const BORDER_DIMMED = '#777'
const CARD_BORDER = '#ccc'
const REASONING_TEXT = '#555'
const REASONING_TEXT_NIGHT = '#ffffff'
const SECTION_DIVIDER = 'rgba(0, 0, 0, 0.08)'
const PLACEHOLDER_BORDER = 'rgba(0, 0, 0, 0.12)'
const FIELD_BG = '#ffffff'

// Delay before smooth-scrolling to a newly active section, letting layout settle first.
export const SCROLL_DELAY_MS = 150

export type OptionState = 'active' | 'selected' | 'chosen' | 'dimmed' | 'placeholder' | 'secondary'

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`

export const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 0;
  border-bottom: 1px solid ${SECTION_DIVIDER};
`

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

export const SectionQuestion = styled.span<{ muted?: boolean }>`
  ${theme.text('normal', 'decorative', 'bold')};
  color: ${(p) => (p.muted ? TITLE_DIMMED : colors.purple20)};
  padding-left: 4px;
`

export const SectionReasoning = styled.span`
  ${theme.text('mediumSmall', 'content')};
  color: ${(p) => (isNightMode(p.theme) ? REASONING_TEXT_NIGHT : REASONING_TEXT)};
  padding-left: 4px;
`

export const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

// 8px vertical gap used between the matched-defect card and the action question.
export const Spacer = styled.div`
  height: 8px;
`

const isEmphasized = (state: OptionState) => {
  return state === 'selected' || state === 'chosen'
}

const isInteractive = (state: OptionState) => {
  return state === 'active' || state === 'secondary' || state === 'chosen'
}

const isPlaceholder = (state: OptionState) => {
  return state === 'placeholder'
}

export const ScrollOptionButton = styled.button<{ optionState: OptionState }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
  box-sizing: border-box;
  /* Selected/chosen get a 4px border; padding shrinks by 3px so the text doesn't jump */
  padding: ${(p) => (isEmphasized(p.optionState) ? '7px 11px' : '10px 14px')};
  border-radius: ${theme.borderRadius.button};
  text-align: left;
  transition: border-color 0.15s;
  background: ${(p) =>
    !isNightMode(p.theme)
      ? colors.white
      : p.optionState === 'dimmed' || p.optionState === 'secondary'
      ? muiColors.backgroundShades[9]
      : colors.blackAbsolute};
  border-style: solid;
  border-width: ${(p) => (isEmphasized(p.optionState) ? '4px' : '1px')};
  border-color: ${(p) =>
    isNightMode(p.theme) || isEmphasized(p.optionState)
      ? colors.purple20
      : p.optionState === 'dimmed' || p.optionState === 'secondary'
      ? BORDER_DIMMED
      : isPlaceholder(p.optionState)
      ? PLACEHOLDER_BORDER
      : colors.purple20};
  opacity: ${(p) => (isPlaceholder(p.optionState) ? 0.2 : 1)};
  cursor: ${(p) => (isInteractive(p.optionState) ? 'pointer' : 'default')};
  pointer-events: ${(p) => (isInteractive(p.optionState) ? 'auto' : 'none')};
`

export const OptionTitle = styled.span<{ dimmed?: boolean }>`
  ${theme.text('normal', 'decorative', 'bold')};
  color: ${(p) => (p.dimmed ? TITLE_DIMMED : colors.purple20)};
`

export const OptionDesc = styled.span<{ dimmed?: boolean }>`
  ${theme.text('mediumSmall', 'content')};
  color: ${(p) => (isNightMode(p.theme) ? colors.white : p.dimmed ? DESC_DIMMED : TEXT_PRIMARY)};
`

export const MatchedDefectCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 14px;
  background: transparent;
  border: 1px solid ${CARD_BORDER};
  border-radius: ${theme.borderRadius.button};
  color: ${(p) => getColor(p.theme, ['black'], ['white'])};
`

export const FieldPair = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

export const FieldLabel = styled.span`
  ${theme.text('normal', 'content', 500)};
`

export const FieldValue = styled.span`
  ${theme.text('normal', 'content', 400)};
`

export const FinishSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 0;
`

export const AI_FIELD_SX = (muiTheme: MuiTheme) => {
  return {
    '& .MuiOutlinedInput-root': {
      backgroundColor: FIELD_BG,
    },
    '& .MuiOutlinedInput-root.Mui-focused fieldset': {
      borderColor: colors.purple20,
    },
    '& textarea': {
      color: muiTheme.palette.mode === 'dark' ? colors.blackAbsolute : undefined,
    },
    '& textarea.Mui-disabled': {
      WebkitTextFillColor: muiTheme.palette.mode === 'dark' ? colors.blackAbsolute : undefined,
    },
    '& textarea::placeholder': {
      color: muiTheme.palette.mode === 'dark' ? colors.blackAbsolute : undefined,
      opacity: muiTheme.palette.mode === 'dark' ? 0.6 : undefined,
    },
  }
}
